import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ArrowRight } from 'lucide-react';
import { useQuest } from '@/context/QuestContext';
import { computeResult } from '@/lib/scoring';
import { api, track } from '@/lib/api';

const STEPS = [
  'Analyzing your strengths…',
  'Mapping your career zones…',
  'Building your future skill stack…',
  'Unlocking your Career Avatar…',
];

export default function Processing() {
  const navigate = useNavigate();
  const { session, setSession } = useQuest();
  const [step, setStep] = useState(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!session.player || session.answers.length < 24) {
      navigate('/');
      return;
    }
    const result = computeResult(session.answers);

    // Set result immediately so Result.jsx renders even if API is slow
    setSession({ ...session, result });

    // Submit lead to backend (fire and forget; capture id when it returns)
    (async () => {
      try {
        const res = await api.post('/leads', {
          player: session.player,
          answers: session.answers,
          result,
          total_xp: 0,
          badges: [],
        });
        setSession((s) => ({ ...s, leadId: res.data.id, result }));
        track('result_generated', { primary_zone: result.primary_zone?.id }, res.data.id);
      } catch (e) {
        // Result is already set locally; lead submission can fail silently for the student
        // but we log so it surfaces during development / monitoring.
        console.error('[processing] lead submission failed:', e?.response?.data || e?.message || e);
      }
    })();

    // Animate steps
    const interval = setInterval(() => {
      setStep((s) => {
        if (s >= STEPS.length - 1) {
          clearInterval(interval);
          setTimeout(() => setReady(true), 600);
          return s;
        }
        return s + 1;
      });
    }, 900);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen gradient-bg grain flex items-center justify-center px-5">
      <div className="max-w-md w-full text-center">
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 160, damping: 16 }}
          className="mx-auto w-32 h-32 rounded-full bg-gradient-to-br from-[#0055FF] via-[#9D4CDD] to-[#FF5A00] flex items-center justify-center shadow-2xl shadow-[#0055FF]/30 animate-pulse-glow"
        >
          <Sparkles className="w-14 h-14 text-white" strokeWidth={2.5} />
        </motion.div>

        <div className="mt-8 space-y-3 min-h-[140px]">
          <AnimatePresence mode="wait">
            {!ready ? (
              <motion.p
                key={step}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="font-display font-black text-ink text-2xl sm:text-3xl"
              >
                {STEPS[step]}
              </motion.p>
            ) : (
              <motion.div key="done" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#0055FF]">All set</p>
                <h2 className="font-display font-black text-ink text-3xl sm:text-4xl mt-1 tracking-tight">Your Future Profile is ready.</h2>
                <button
                  onClick={() => navigate('/result')}
                  data-testid="reveal-result-button"
                  className="btn-primary mt-6 inline-flex items-center justify-center gap-2 w-full"
                >
                  Reveal My Result <ArrowRight className="w-4 h-4" strokeWidth={3} />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
